import clsx from 'clsx';
import { UserGroupIcon, UsersIcon } from '@heroicons/react/24/solid';
import type { Conversation } from '@/hooks/useAllConversations';
import Avatar from '@/components/Avatar';
import { formatConversationTime } from '@/lib/chatTime';

interface Props {
  conversation: Conversation;
  selected: boolean;
  onSelect: (conversation: Conversation) => void;
  /** Indent inside the org/group tree. */
  indent?: boolean;
}

export default function ConversationListItem({ conversation, selected, onSelect, indent }: Props) {
  const { name, type, lastMessageText, lastMessageUserName, lastMessageAt, unreadCount, avatarUrl } =
    conversation;

  const preview = lastMessageText
    ? lastMessageUserName
      ? `${lastMessageUserName}: ${lastMessageText}`
      : lastMessageText
    : 'No messages yet';

  return (
    <button
      onClick={() => onSelect(conversation)}
      className={clsx(
        'flex w-full items-center gap-3 rounded-lg py-2.5 pr-3 text-left transition',
        indent ? 'pl-6' : 'pl-3',
        selected ? 'bg-flock-navy-100' : 'hover:bg-flock-navy-50'
      )}
    >
      {type === 'dm' ? (
        <Avatar src={avatarUrl} name={name} sizeClass="h-11 w-11" textClass="text-sm" />
      ) : (
        <div
          className={clsx(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white',
            type === 'everyone' ? 'bg-flock-navy-600' : 'bg-flock-navy-500'
          )}
        >
          {type === 'everyone' ? <UserGroupIcon className="h-6 w-6" /> : <UsersIcon className="h-6 w-6" />}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={clsx(
              'truncate text-sm',
              unreadCount > 0 ? 'font-semibold text-flock-navy-900' : 'font-medium text-flock-navy-800'
            )}
          >
            {name}
          </span>
          <span className="shrink-0 text-xs text-flock-navy-400">
            {formatConversationTime(lastMessageAt)}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span
            className={clsx(
              'truncate text-xs',
              unreadCount > 0 ? 'font-medium text-flock-navy-700' : 'text-flock-navy-400'
            )}
          >
            {preview}
          </span>
          {unreadCount > 0 && (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-flock-navy-600 px-1.5 text-xs font-semibold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
